// import { observable } from 'mobx';

import { CombinedKeyMap } from 'app/libs/combined-key-map/combined-key-map';

import { TTariff } from '../../core/tariff';
import { TTariffGrid } from '../../core/tariff-grid';
import { TTariffType } from '../../core/tariff-type';
import { TTariffZone } from '../../core/tariff-zone';
import { TTrainType } from '../../core/train-type';

type RecordKeys = {
  trainTypeId: TTrainType['id'];
  tariffTypeId: TTariffType['id'];
  zoneId: TTariffZone['id'];
};

export const createDataMapTariff = (data: TTariffGrid) => {
  // 0. Create key builder for DataMap
  const createCombinedKeyMapKeyer = CombinedKeyMap.createKeyBuilder<RecordKeys>(
    ['tariffTypeId', 'trainTypeId', 'zoneId'],
  );
  type DataMapKey = ReturnType<typeof createCombinedKeyMapKeyer>;

  // 1. Create empty records with all possible combinations based on train type, tariff type and zone
  type EmptyTariffFields = Pick<
    TTariff,
    'trainType' | 'tariffType' | 'zone' | 'maxCountPerTicket' | 'price'
  >;
  type EmptyRecordValue = EmptyTariffFields & { isEmpty: boolean };
  type EmptyRecords = Array<[key: DataMapKey, value: EmptyRecordValue]>;

  // 1.1 Initialize empty array for all possible combinations.
  // Will be used to create Map
  const emptyRecords: EmptyRecords = [];

  // 1.2 Create all possible combinations
  data.trainTypes.forEach((trainType) => {
    data.tariffTypes.forEach((tariffType) => {
      data.tariffZones.forEach((zone) => {
        const ids = {
          trainTypeId: trainType.id,
          tariffTypeId: tariffType.id,
          zoneId: zone.id,
        };

        const record = {
          isEmpty: true, // for differ new records and existing
          trainType,
          tariffType,
          zone,
          price: 0,
          maxCountPerTicket: 0,
        };

        const key = createCombinedKeyMapKeyer(ids);

        emptyRecords.push([key, record]);
      });
    });
  });

  // 2. Create Map with empty records
  const emptyDataMap = new Map(emptyRecords);

  // 3. Fill Map with existing records from server
  data.tariffs.forEach((row) => {
    const ids: RecordKeys = {
      tariffTypeId: row.tariffType.id,
      trainTypeId: row.trainType.id,
      zoneId: row.zone.id,
    };

    const key = createCombinedKeyMapKeyer(ids);

    emptyDataMap.set(key, { isEmpty: false, ...row });
  });

  // 4. Initiate CombinedKeyMap
  const combinedKeyMap = new CombinedKeyMap({
    data: emptyDataMap,
    keyBuilder: createCombinedKeyMapKeyer,
  });

  return combinedKeyMap;
};

export type TariffDataMap = ReturnType<typeof createDataMapTariff>;
